import { useInsuranceHikiukeDetail } from '../../hooks/features/insuranceHikiukeDetail';
import {
  LoadingSpinner,
  ErrorPage,
  Toolbar,
  PdfViewer,
} from '../../components/features/insuranceHikiukeDetail';
import FeedbackPopup from '../../components/popups/FeedbackPopup';
import BadReasonFeedbackPopup from '../../components/popups/BadReasonFeedbackPopup';
import { formatDateAsEdition } from '../../utils/dateFormatter';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import '../../styles/InsuranceHikiukeDetail.css';

/**
 * 傷病名タブに表示する文字数上限
 */
const DISEASE_NAME_TRUNCATE_LIMIT = 10;
const SHORT_DISEASE_NAME_LIMIT = 6;

/**
 * 傷病名タブの表示用に文字列を省略する
 *
 * @param {string} name - 傷病名
 * @returns {string} 表示用の傷病名
 */
const truncateDiseaseName = (name = '') => (
  name.length > DISEASE_NAME_TRUNCATE_LIMIT
    ? `${name.slice(0, DISEASE_NAME_TRUNCATE_LIMIT)}...`
    : name
);

/**
 * 引受目安詳細ページコンポーネント
 * ViewとContainerを統合し、分割されたコンポーネントを組み合わせて表示
*/
const InsuranceHikiukeDetail = () => {
  const vm = useInsuranceHikiukeDetail();

  const {
    loading,
    showErrorPage,
    errorMessage,
    isMobile,
    zoomLevel,
    currentPage,
    totalPages,
    isRendering,
    isLandscape,
    pdfFile,
    pdfOptions,
    currentPopup,
    isFeedbackSubmitting,
    activeJumpButton,
    groupedResults,
    activeCompany,
    activeCompanyResults,
    selectedDiseaseByCompany,
    canJumpDisease,
    canJumpContents,
    canJumpLegend,
    canJumpAppendix,
    highlightsPolygon,
    highlightCanvasRef,
    pdfContainerRef,
    pdfScrollContainerRef,
    isPdfDragging,
    pdfInteractionMode,
    setPdfInteractionMode,
    handlePdfMouseDown,
    handlePdfMouseMove,
    handlePdfMouseUp,
    handlePdfMouseLeave,
    handleZoomIn,
    handleZoomOut,
    handlePrevPage,
    handleNextPage,
    pageInputValue,
    handleChangePageInput,
    handlePageInputKeyDown,
    handlePageInputBlur,
    handleRetry,
    handleBackToSearch,
    detailResult,
    handleClose,
    handleFeedbackClose,
    handleGoodFeedback,
    handleBadFeedback,
    handleFeedbackSubmit,
    handleFeedbackBack,
    handleJumpDisease,
    handleJumpContents,
    handleJumpLegends,
    handleJumpAppendix,
    activateCompany,
    activateDisease,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    onPageLoadSuccess,
    onPageRenderSuccess,
    onPageLoadError,
    searchText,
    setSearchText,
    searchResults,
    currentSearchIndex,
    isSearching,
    searchError,
    isSearchOpen,
    setIsSearchOpen,
    handleSearch,
    handleNextSearchResult,
    handlePrevSearchResult,
    handleClearSearch,
    customTextRenderer,
    onGetTextSuccess,
    onTextLayerRenderSuccess,
    pdfDocument,
  } = vm;

  const useThreeColumnDiseaseTabs = activeCompanyResults.length > 0
    && activeCompanyResults.every(
      (result) => result.diseaseName.length <= SHORT_DISEASE_NAME_LIMIT
    );

  const documentEdition = formatDateAsEdition(detailResult?.documentEffectiveDate)
    || formatDateAsEdition(detailResult?.pdfUrl);

    // ローディング表示
  if (loading) {
    return (
      <div
        className="insurance-detail-page hikiuke-meyasu-detail-page"
        style={{ overflow: 'hidden', height: '100vh', position: 'relative' }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div
      className="insurance-detail-page hikiuke-meyasu-detail-page"
      style={{ overflow: 'hidden', height: '100vh', position: 'relative' }}
    >
      <div
        className={`detail-container ${isLandscape ? 'landscape-layout' : 'portrait-layout'}`}
        style={{ overflow: 'hidden', height: '100%' }}
      >
        <div className="pdf-viewer-container" style={{ overflow: 'hidden', height: '100%' }}>
          {showErrorPage ? (
            <ErrorPage
              errorMessage={errorMessage}
              onRetry={handleRetry}
              onBackToSearch={handleBackToSearch}
            />
          ) : (
            <>
              {/* 保険会社タブ */}
              <div className="hikiuke-tab-container">
                <div className="hikiuke-company-tabs" role="tablist" aria-label="保険会社タブ">
                  {groupedResults.map((group) => (
                    <button
                      key={group.companyName}
                      type="button"
                      role="tab"
                      className={`hikiuke-company-tab ${activeCompany === group.companyName ? 'active' : ''}`}
                      aria-selected={activeCompany === group.companyName}
                      onClick={() => activateCompany(group.companyName)}
                    >
                      {group.companyName}
                    </button>
                  ))}
                </div>
                {/* 閉じるボタン */}
                <button onClick={handleClose} className="detail-close-button" title="閉じる" type="button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>

              </div>

              {/* 傷病名タブ */}
              <div className="hikiuke-disease-panel">
                <div
                  className={`hikiuke-disease-tabs ${useThreeColumnDiseaseTabs ? 'hikiuke-disease-tabs--three-columns' : ''}`}
                  role="tablist"
                  aria-label="傷病名タブ"
                >
                  {activeCompanyResults.map((result) => (
                    <button
                      key={result._key}
                      type="button"
                      role="tab"
                      className={[
                        'hikiuke-disease-tab',
                        selectedDiseaseByCompany[activeCompany] === result._key ? 'active' : '',
                      ].filter(Boolean).join(' ')}
                      aria-selected={selectedDiseaseByCompany[activeCompany] === result._key}
                      onClick={() => activateDisease(activeCompany, result._key)}
                      title={result.diseaseName.length > DISEASE_NAME_TRUNCATE_LIMIT ? result.diseaseName : undefined}
                    >
                      {truncateDiseaseName(result.diseaseName)}
                    </button>
                  ))}
                </div>

                {/* 文書適用日 */}
                <div className="hikiuke-document-meta">
                  <span className="hikiuke-document-meta-item">
                    {`文書適用日：${documentEdition || '—'}`}
                  </span>
                </div>
              </div>

              {/* ツールバー（モバイル検索アコーディオンを含む） */}
              <Toolbar
                isMobile={isMobile}
                showGuardWarning={detailResult?.underGuardControl === true}
                activeJumpButton={activeJumpButton}
                canJumpDisease={canJumpDisease}
                canJumpContents={canJumpContents}
                canJumpLegend={canJumpLegend}
                canJumpAppendix={canJumpAppendix}
                onJumpCurrentDisease={handleJumpDisease}
                onJumpContents={handleJumpContents}
                onJumpLegend={handleJumpLegends}
                onJumpAppendix={handleJumpAppendix}
                zoomLevel={zoomLevel}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                interactionMode={pdfInteractionMode}
                onToggleInteractionMode={() => setPdfInteractionMode(prev => prev === 'drag' ? 'select' : 'drag')}
                currentPage={currentPage}
                totalPages={totalPages}
                isRendering={isRendering}
                onPrevPage={handlePrevPage}
                onNextPage={handleNextPage}
                pageInputValue={pageInputValue}
                onChangePageInput={handleChangePageInput}
                onPageInputKeyDown={handlePageInputKeyDown}
                onPageInputBlur={handlePageInputBlur}
                searchText={searchText}
                onSearchTextChange={setSearchText}
                searchResults={searchResults}
                currentSearchIndex={currentSearchIndex}
                isSearching={isSearching}
                isSearchOpen={isSearchOpen}
                onToggleSearchOpen={() => setIsSearchOpen(prev => !prev)}
                onSearch={handleSearch}
                onNextSearchResult={handleNextSearchResult}
                onPrevSearchResult={handlePrevSearchResult}
                onClearSearch={handleClearSearch}
                pdfDocument={pdfDocument}
                searchError={searchError}
              />

              {/* PDFビューワー */}
              <PdfViewer
                ref={pdfContainerRef}
                scrollContainerRef={pdfScrollContainerRef}
                isDragging={isPdfDragging}
                interactionMode={pdfInteractionMode}
                onMouseDown={handlePdfMouseDown}
                onMouseMove={handlePdfMouseMove}
                onMouseUp={handlePdfMouseUp}
                onMouseLeave={handlePdfMouseLeave}
                pdfFile={pdfFile}
                pdfOptions={pdfOptions}
                currentPage={currentPage}
                zoomLevel={zoomLevel}
                isRendering={isRendering}
                highlightsPolygon={highlightsPolygon}
                highlightCanvasRef={highlightCanvasRef}
                onDocumentLoadSuccess={onDocumentLoadSuccess}
                onDocumentLoadError={onDocumentLoadError}
                onPageLoadSuccess={onPageLoadSuccess}
                onPageRenderSuccess={onPageRenderSuccess}
                onPageLoadError={onPageLoadError}
                customTextRenderer={customTextRenderer}
                onGetTextSuccess={onGetTextSuccess}
                onRenderTextLayerSuccess={onTextLayerRenderSuccess}
              />

              {/* 注意書き */}
              {!detailResult?.fromDocumentCatalog && (
                <>
                  <div>
                    ※ 正確な取扱いや判断については、各保険会社へご確認ください。
                  </div>
                  <div>
                    ※ 本ページ以外にも関連内容が記載されている可能性があります。必要に応じて前後ページもご参照ください。
                  </div>
                </>
              )}

            </>
          )}
        </div>
      </div>

      {/* フィードバックポップアップ */}
      {!detailResult?.fromDocumentCatalog && currentPopup === 'feedback' && (
        <FeedbackPopup
          onClose={handleFeedbackClose}
          handleGoodSubmit={handleGoodFeedback}
          handleBadSubmit={handleBadFeedback}
          isSubmitting={isFeedbackSubmitting}
        />
      )}

      {!detailResult?.fromDocumentCatalog && currentPopup === 'badReason' && (
        <BadReasonFeedbackPopup
          onClose={handleFeedbackClose}
          onBack={handleFeedbackBack}
          handleSubmit={handleFeedbackSubmit}
          isSubmitting={isFeedbackSubmitting}
        />
      )}
    </div>
  );
};

export default InsuranceHikiukeDetail;
